import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckArrowRight, faStopwatch, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { useAlert } from '../../providers/AlertProvider';

/**
 * SubbedTable — displays subcontracted routes at the bottom of the route view.
 * Shows company, ref, price inline per row.
 */
const SubbedTable = ({
    routes,
    theme,
    isLocked,
    showCostings,
    showDemurrage,
    showDeliveryDetails,
    showJobComments,
    showJobCostings,
    showStartTime,
    deliveryAlerts,
    toggleConfirmed,
    contractRoute,
    editSubcontract,
    canEdit,
    onPodOff,
    deleteRoute,
    moveRoute,
    completedRouteUuids,
    onViewCompletion,
    showCustomerConsignment,
    showNetworkFreightMetrics,
}) => {
    const { showAlert } = useAlert();
    const [moveUuid, setMoveUuid] = useState(null);
    const [moveDate, setMoveDate] = useState('');

    const handleTruckClick = (route_uuid) => {
        showAlert({
            message: 'Change Status?',
            type: 'success',
            buttons: [
                { type: 'primary',   label: 'Unsub', onClick: () => contractRoute(route_uuid, false) },
                { type: 'warning',   label: 'Edit',  onClick: () => editSubcontract(route_uuid) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const formatTimeToHHMM = (t) => {
        if (!t) return null;
        return t.length <= 5 ? t : t.substring(0, 5);
    };

    if (!routes || routes.length === 0) return null;

    const boxBorder = theme === 'dark' ? '2px solid rgba(252,186,63,0.7)' : '2px solid rgba(180,130,0,0.6)';
    const boxBg = theme === 'dark' ? '#1e1a0a' : '#fffbea';
    const headerBg = theme === 'dark' ? 'rgba(252,186,63,0.15)' : 'rgba(252,186,63,0.25)';

    return (
        <div style={{ border: boxBorder, borderRadius: 8, marginBottom: 14, overflow: 'hidden', background: boxBg }}>
            {/* Move-to-date overlay */}
            {moveUuid && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setMoveUuid(null)}
                >
                    <div style={{ background: theme === 'dark' ? '#222' : '#fff', borderRadius: 10, padding: 24, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 style={{ margin: '0 0 16px', color: theme === 'dark' ? '#fff' : '#222' }}>Move job to date</h4>
                        <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)}
                            style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginBottom: 16, fontSize: 15 }}
                        />
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setMoveUuid(null)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer', color: theme === 'dark' ? '#ccc' : '#555' }}>Cancel</button>
                            <button onClick={() => { if (moveDate) { moveRoute(moveUuid, moveDate); setMoveUuid(null); setMoveDate(''); } }} disabled={!moveDate} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: moveDate ? '#6366f1' : '#aaa', color: '#fff', cursor: moveDate ? 'pointer' : 'not-allowed' }}>Move</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <div style={{
                background: headerBg,
                padding: '8px 14px',
                fontWeight: 700,
                fontSize: 15,
                borderBottom: boxBorder,
                color: theme === 'dark' ? 'rgba(252,186,63,0.95)' : 'rgba(140,100,0,0.9)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
            }}>
                <FontAwesomeIcon icon={faTruckArrowRight} style={{ width: 18 }} />
                Subcontracted — {routes.length} job{routes.length !== 1 ? 's' : ''}
            </div>

            <table className="route-table" style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(252,186,63,0.9)' }} icon={faTruckArrowRight} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(255,0,0,1)' }} icon={faStopwatch} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.2)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(26,157,215,0.95)' }} icon={faClipboardList} />
                        </th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Customer</th>
                        {showCustomerConsignment && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Customer Ref</th>}
                        {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Pallets</th>}
                        {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Weight</th>}
                        {showNetworkFreightMetrics && showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>ADR</th>}
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Route</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Job Comments</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Company</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Ref</th>
                        <th style={{ background: 'rgba(0,0,0,0.2)' }}>Price</th>
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Consignment</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.2)' }}>Costings</th>}
                    </tr>
                </thead>
                <tbody>
                    {routes.map((route, index) => {
                        const {
                            route_uuid, job_comments, route: routeDetails,
                            route_ref, demurrage, demurrage_cost, costings,
                            subbed_company, subbed_ref, subbed_price, customer_company,
                            customer_consignment_number, pallet_count, weight, is_network_freight, adr_value,
                        } = route;

                        const isCompleted = !!completedRouteUuids?.[route_uuid];
                        const rowBg = isCompleted
                            ? index % 2 === 0 ? 'rgba(26,157,215,0.9)' : 'rgba(56,189,248,0.8)'
                            : index % 2 === 0
                                ? 'rgba(252,186,63,0.55)'
                                : 'rgba(252,186,63,0.35)';

                        return (
                            <tr
                                key={route_uuid}
                                className={showNetworkFreightMetrics && is_network_freight && adr_value ? 'network-adr-row' : undefined}
                                style={{ background: rowBg, color: '#333' }}
                            >
                                {/* Sub status / toggle */}
                                <td className="fixed-th" style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                    title="Subcontracted (click for options)"
                                    onClick={() => {
                                        if (isCompleted) {
                                            onViewCompletion(route_uuid, { job_comments, route_ref });
                                        } else if (!isLocked) {
                                            handleTruckClick(route_uuid);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon style={{ width: 20, color: 'rgba(140,100,0,0.9)' }} icon={faTruckArrowRight} />
                                </td>

                                {/* Demurrage */}
                                <td className="fixed-th" style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                    title={demurrage ? `${demurrage}: £${demurrage_cost}` : 'Add Demurrage'}
                                    onClick={() => { if (!isLocked) showDemurrage(route_uuid); }}
                                >
                                    <FontAwesomeIcon style={{ width: 20, color: demurrage ? '#ff0000' : 'rgba(0,0,0,0.5)' }} icon={faStopwatch} />
                                </td>

                                {/* Delivery details */}
                                <td className="fixed-th" style={{ width: 40, cursor: 'pointer' }}
                                    title="View / Edit Delivery Details"
                                    onClick={() => showDeliveryDetails(route_uuid)}
                                >
                                    <FontAwesomeIcon icon={faClipboardList} style={{ width: 20, color: deliveryAlerts?.has(route_uuid) ? 'rgba(220,53,69,0.95)' : 'rgba(0,0,0,0.5)' }} />
                                </td>

                                {/* Customer — route actions */}
                                <td className={!isLocked ? 'cell-hoverable' : ''}>
                                    <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        onClick={() => {
                                            if (!isLocked) showAlert({
                                                message: 'What do you want to do with this job?',
                                                type: 'warning',
                                                buttons: [
                                                    { label: 'Delete', type: 'danger', onClick: () => deleteRoute(route_uuid) },
                                                    { label: 'Move', type: 'primary', onClick: () => { setMoveDate(''); setMoveUuid(route_uuid); } },
                                                    { label: 'Cancel', type: 'secondary' },
                                                ],
                                            });
                                        }}
                                    >{customer_company || '—'}</span>
                                </td>

                                {showCustomerConsignment && <td>{customer_consignment_number || '----'}</td>}
                                {showNetworkFreightMetrics && <td>{pallet_count ?? '----'}</td>}
                                {showNetworkFreightMetrics && <td>{weight == null ? '----' : `${Number(weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg`}</td>}
                                {showNetworkFreightMetrics && showCostings && <td>{is_network_freight ? adr_value || '----' : '----'}</td>}

                                {/* Route */}
                                <td>{routeDetails || 'No route details'}</td>

                                {/* Job Comments */}
                                <td className={!isLocked ? 'cell-hoverable' : ''} style={{ maxWidth: 500, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                    <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        onClick={() => { if (!isLocked) showJobComments(route_uuid, job_comments); }}
                                    >{job_comments || <span style={{ opacity: 0.35 }}>----</span>}</span>
                                </td>

                                {/* Company */}
                                <td><strong>{subbed_company || 'N/A'}</strong></td>

                                {/* Ref */}
                                <td>{subbed_ref || '—'}</td>

                                {/* Price */}
                                <td><strong>£{subbed_price || '0.00'}</strong></td>

                                {showCostings && <td>{route_ref || '----'}</td>}
                                {showCostings && (
                                    <td className="cell-hoverable">
                                        <span style={{ cursor: 'pointer' }}
                                            onClick={() => showJobCostings(route_uuid, costings)}
                                        >{costings ? '£' + costings : '----'}</span>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default SubbedTable;
